import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, userAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is logged in on mount
    const token = localStorage.getItem('token');
    if (token) {
      loadUser().catch(() => {
        // If loadUser fails, just set loading to false
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const { data } = await userAPI.getProfile();
      setUser(data.data);
      setLoading(false);
      return { success: true, user: data.data };
    } catch (err) {
      // Only log error if it's not a 401 (unauthorized) error
      if (err.response?.status !== 401) {
        console.error('Failed to load user:', err);
      }
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setLoading(false);
      throw err; // Throw error so caller can handle it
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const { data } = await authAPI.login({ email, password });

      // Store tokens
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('refreshToken', data.data.refreshToken);

      // Set user
      setUser(data.data.user);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (email, password, full_name, verification_code) => {
    try {
      setError(null);
      const { data } = await authAPI.register({ email, password, full_name, verification_code });

      // Store tokens
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('refreshToken', data.data.refreshToken);

      // Set user
      setUser(data.data.user);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error?.message || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local storage and state regardless of API response
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setUser(null);
    }
  };

  const updateUser = (userData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    loadUser,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
